import { useCallback, useEffect, useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link as MuiLink,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  Home as HomeIcon,
  MoreVert as MoreVertIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { teacherBalanceApi } from '@/api/teacherBalance'
import { formatMoney } from '@/components/billing/formatMoney'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'
import { ROUTES } from '@/utils/constants'
import type { BalanceTopupRequest, TopupRequestStatus } from '@/types/tts'
import { getUserDisplayName } from '@/utils/userName'
import '@/styles/dashboard.css'

const STATUS_COLORS: Record<TopupRequestStatus, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  confirmed: 'success',
  rejected: 'error',
  cancelled: 'default',
}

type DecisionAction = 'confirm' | 'reject'

export function AdminTopupRequestsPage() {
  const { t } = useTranslation()
  const [requests, setRequests] = useState<BalanceTopupRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<TopupRequestStatus | 'all'>('pending')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [total, setTotal] = useState(0)
  const [decision, setDecision] = useState<{ request: BalanceTopupRequest; action: DecisionAction } | null>(null)
  const [decisionNote, setDecisionNote] = useState('')
  const [deciding, setDeciding] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuRequest, setMenuRequest] = useState<BalanceTopupRequest | null>(null)

  const loadRequests = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await teacherBalanceApi.adminListTopupRequests(
        statusFilter === 'all' ? undefined : statusFilter,
        page,
        perPage,
      )
      setRequests(result.data)
      setTotal(result.meta?.total ?? result.data.length)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page, perPage, t])

  useEffect(() => {
    void loadRequests()
  }, [loadRequests])

  const openDecision = (request: BalanceTopupRequest, action: DecisionAction) => {
    setMenuAnchor(null)
    setMenuRequest(null)
    setDecision({ request, action })
    setDecisionNote('')
  }

  const handleDecide = async () => {
    if (!decision) return
    setDeciding(true)
    setError(null)
    try {
      if (decision.action === 'confirm') {
        await teacherBalanceApi.adminConfirmTopupRequest(decision.request.id, decisionNote || undefined)
      } else {
        await teacherBalanceApi.adminRejectTopupRequest(decision.request.id, decisionNote || undefined)
      }
      setDecision(null)
      await loadRequests()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setDeciding(false)
    }
  }

  const userName = (request: BalanceTopupRequest) =>
    request.user?.name || getUserDisplayName(request.user ?? { id: request.user_id })

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">
            {t('admin.topupRequests.title', 'Заявки на пополнение')}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('admin.topupRequests.title', 'Заявки на пополнение')}
          </Typography>
          <PageHelpPopover pageKey="admin-topup-requests" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
          <InputLabel>{t('admin.topupRequests.statusFilter', 'Статус')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('admin.topupRequests.statusFilter', 'Статус')}
            onChange={(e) => {
              setPage(1)
              setStatusFilter(e.target.value as TopupRequestStatus | 'all')
            }}
          >
            <MenuItem value="all">{t('admin.topupRequests.statusAll', 'Все')}</MenuItem>
            <MenuItem value="pending">{t('balance.topup.statuses.pending', 'Ожидает')}</MenuItem>
            <MenuItem value="confirmed">{t('balance.topup.statuses.confirmed', 'Подтверждена')}</MenuItem>
            <MenuItem value="rejected">{t('balance.topup.statuses.rejected', 'Отклонена')}</MenuItem>
            <MenuItem value="cancelled">{t('balance.topup.statuses.cancelled', 'Отменена')}</MenuItem>
          </Select>
        </FormControl>

        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : requests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {t('balance.topup.empty', 'Заявок пока нет')}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('balance.topup.createdAt', 'Дата')}</TableCell>
                      <TableCell>{t('admin.topupRequests.teacher', 'Учитель')}</TableCell>
                      <TableCell>{t('balance.topup.amount', 'Сумма, ₽')}</TableCell>
                      <TableCell>{t('balance.topup.paymentMethod', 'Способ оплаты')}</TableCell>
                      <TableCell>{t('balance.topup.note', 'Комментарий')}</TableCell>
                      <TableCell>{t('balance.topup.status', 'Статус')}</TableCell>
                      <TableCell align="right">{t('admin.users.actions', 'Действия')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.id} hover>
                        <TableCell>
                          {formatDateDMY(request.created_at)}
                        </TableCell>
                        <TableCell>{userName(request)}</TableCell>
                        <TableCell>{formatMoney(request.amount, request.currency)}</TableCell>
                        <TableCell>
                          {t(`balance.topup.methods.${request.payment_method}`, request.payment_method)}
                        </TableCell>
                        <TableCell>
                          {request.note ?? '—'}
                          {request.proof_url && (
                            <>
                              {' '}
                              <MuiLink href={request.proof_url} target="_blank" rel="noopener">
                                {t('admin.topupRequests.proof', 'чек')}
                              </MuiLink>
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={STATUS_COLORS[request.status]}
                            label={t(`balance.topup.statuses.${request.status}`, request.status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {request.status === 'pending' && (
                            <IconButton
                              size="small"
                              aria-label={t('admin.users.actions', 'Действия')}
                              onClick={(e) => {
                                setMenuAnchor(e.currentTarget)
                                setMenuRequest(request)
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => {
            setMenuAnchor(null)
            setMenuRequest(null)
          }}
        >
          <MenuItem
            onClick={() => menuRequest && openDecision(menuRequest, 'confirm')}
          >
            {t('admin.topupRequests.confirm', 'Подтвердить')}
          </MenuItem>
          <MenuItem
            onClick={() => menuRequest && openDecision(menuRequest, 'reject')}
          >
            {t('admin.topupRequests.reject', 'Отклонить')}
          </MenuItem>
        </Menu>

        {!loading && total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={Math.max(0, page - 1)}
            onPageChange={(_, p) => setPage(p + 1)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(parseInt(e.target.value, 10))
              setPage(1)
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage={t('words.rows_per_page')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t('words.of')} ${count !== -1 ? count : `${t('words.more_than')} ${to}`}`
            }
          />
        )}

        <Dialog open={decision !== null} onClose={() => setDecision(null)} maxWidth="xs" fullWidth>
          <DialogTitle>
            {decision?.action === 'confirm'
              ? t('admin.topupRequests.confirmTitle', 'Подтвердить заявку?')
              : t('admin.topupRequests.rejectTitle', 'Отклонить заявку?')}
          </DialogTitle>
          <DialogContent>
            {decision && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {userName(decision.request)} — {formatMoney(decision.request.amount, decision.request.currency)}
              </Typography>
            )}
            <TextField
              label={t('admin.topupRequests.decisionNote', 'Комментарий к решению')}
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              size="small"
              inputProps={{ maxLength: 1000 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDecision(null)}>{t('common.cancel', 'Отмена')}</Button>
            <Button
              variant="contained"
              color={decision?.action === 'confirm' ? 'success' : 'error'}
              disabled={deciding}
              onClick={handleDecide}
            >
              {decision?.action === 'confirm'
                ? t('admin.topupRequests.confirm', 'Подтвердить')
                : t('admin.topupRequests.reject', 'Отклонить')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
